import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { homeAPI } from '../../services/api';
import '../../App.css';
import './Home.css';
import '../../styles/Cards.css';
import HeroSection from '../../components/shared/HeroSection';
import Cards from '../../components/common/UI/Cards';
import Footer from '../../components/common/Footer/Footer';
import Members from '../Members/Members';
import PartnersSection from '../../components/features/Partners/PartnersSection';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getCountryName } from '../../utils/locationUtils';
import EventPartners from '../../components/features/Events/EventPartners';

function Home() {
  const [pageData, setPageData] = useState({
    activities: [],
    members: [],
    partners: [],
    hero: {},
    upcomingEvents: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const eventsGridRef = useRef(null);
  const [countdowns, setCountdowns] = useState({});
  const navigate = useNavigate();

  const isAppCheckAuthError = (err) => {
    const message = String(err?.message || '').toLowerCase();
    const code = String(err?.code || '').toLowerCase();
    return (
      code.includes('unauthenticated') ||
      code.includes('permission-denied') ||
      message.includes('401') ||
      message.includes('unauthenticated') ||
      message.includes('app check')
    );
  };

  // Function to calculate countdown for each event
  const calculateCountdown = (eventDate) => {
    const now = new Date();
    const eventTime = new Date(eventDate);
    const timeRemaining = eventTime - now;
    
    if (timeRemaining <= 0) {
      return { days: 0, hours: 0, minutes: 0 };
    }
    
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes };
  };

  // Update countdowns every minute
  useEffect(() => {
    if (pageData.upcomingEvents && pageData.upcomingEvents.length > 0) {
      // Initial calculation
      const initialCountdowns = {};
      pageData.upcomingEvents.forEach(event => {
        initialCountdowns[event.id] = calculateCountdown(event.date);
      });
      setCountdowns(initialCountdowns);
      
      // Set interval to update countdowns
      const interval = setInterval(() => {
        const updatedCountdowns = {};
        pageData.upcomingEvents.forEach(event => {
          updatedCountdowns[event.id] = calculateCountdown(event.date);
        });
        setCountdowns(updatedCountdowns);
      }, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [pageData.upcomingEvents]);

  // Function to scroll the events horizontally
  const scrollEvents = (direction) => {
    if (eventsGridRef.current) {
      const cards = eventsGridRef.current.querySelectorAll('.home-event-card');
      if (cards.length === 0) return;
      
      // Get the current scroll position and card width
      const containerWidth = eventsGridRef.current.clientWidth;
      const cardWidth = cards[0].offsetWidth;
      const currentScroll = eventsGridRef.current.scrollLeft;
      
      // Calculate which card is most visible
      const currentCardIndex = Math.round(currentScroll / (cardWidth + 32)); // 32px is the gap
      
      // Calculate the next card index
      const nextCardIndex = direction === 'right' 
        ? Math.min(currentCardIndex + 1, cards.length - 1)
        : Math.max(currentCardIndex - 1, 0);
      
      // Calculate the target scroll position
      const targetScroll = nextCardIndex * (cardWidth + 32);
      
      // Scroll to the target position
      eventsGridRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  const fetchHomePageData = async () => {
    try {
      setLoading(true);
      setError(null);


      // Fetch all data from getHomePageData
      const response = await homeAPI.getHomePageData();

      if (!response) {
        throw new Error('No data received from server');
      }

      setPageData({
        activities: response.recentActivities || [],
        members: response.members || [],
        upcomingEvents: response.upcomingEvents || [],
        hero: {
          title: 'Welcome to FOMEJE',
          subtitle: 'Empowering Youth In The Mediterranean',
          backgroundImage: '/images/default-hero.jpg'
        }
      });
    } catch (err) {
      // Set default data to prevent empty page
      setPageData({
        activities: [],
        members: [],
        partners: [],
        hero: {
          title: 'Welcome to FOMEJE',
          subtitle: 'Empowering Youth In The Mediterranean',
          backgroundImage: '/images/default-hero.jpg'
        },
        upcomingEvents: []
      });

      // Home data is public; if backend rejects due to App Check/auth,
      // render fallback content instead of blocking the whole page.
      if (isAppCheckAuthError(err)) {
        setError(null);
      } else {
        setError(err.message || 'Failed to load home page data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomePageData();
  }, []);

  // Ensure cards are centered on initial load
  useEffect(() => {
    if (eventsGridRef.current && pageData.upcomingEvents && pageData.upcomingEvents.length > 0) {
      // Add a small delay to ensure the cards are rendered
      setTimeout(() => {
        // Center the first card
        const firstCard = eventsGridRef.current.querySelector('.event-card');
        if (firstCard) {

          
          // Adjust for the extra space added by the pseudo-elements
          eventsGridRef.current.scrollLeft = 0;
        }
      }, 100);
    }
  }, [pageData.upcomingEvents]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button 
          onClick={fetchHomePageData}
          className="retry-button"
        >
          Retry Now
        </button>
      </div>
    );
  }

  // Limit to 5 upcoming events for the slider
  const upcomingEventsToShow = pageData.upcomingEvents?.slice(0, 5) || [];

  return (
    <div className="home-container">
      {/* Hero Section */}
      <HeroSection />

      {/* Upcoming Events Section */}
      {upcomingEventsToShow && upcomingEventsToShow.length > 0 && (
        <div className="upcoming-events-section">
          <div className="section-header">
            <h2>Upcoming Events</h2>
            <Link to="/activities" className="view-all-link">
              View All <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          
          <div className="events-grid-container">
            {/* Side navigation buttons (for larger screens) */}
            {upcomingEventsToShow.length > 1 && (
              <>
                <button 
                  className="slider-nav-button slider-nav-prev" 
                  onClick={() => scrollEvents('left')}
                  aria-label="Previous event"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button 
                  className="slider-nav-button slider-nav-next" 
                  onClick={() => scrollEvents('right')}
                  aria-label="Next event"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </>
            )}
            
            <div className="events-grid" ref={eventsGridRef}>
              {upcomingEventsToShow.map(event => (
                <div key={event.id} className="home-event-card">
                  <div className="home-event-content">
                    <div className="home-event-image">
                      {/* Title overlay in middle of image */}
                      <div className="home-event-image-title">
                        <h3>{event.title}</h3>
                      </div>
                      
                      <img 
                        src={event.imageUrl || '/default-activity.jpg'} 
                        alt={event.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/default-activity.jpg';
                        }}
                      />
                      
                      {/* Countdown Badge at bottom of image */}
                      {countdowns[event.id] && (
                        <div className="home-countdown-badge">
                          {countdowns[event.id].days > 0 && (
                            <span className="home-countdown-item">
                              <span className="home-countdown-value">{countdowns[event.id].days}</span>
                              <span className="home-countdown-label">days</span>
                            </span>
                          )}
                          <span className="home-countdown-item">
                            <span className="home-countdown-value">{countdowns[event.id].hours}</span>
                            <span className="home-countdown-label">hrs</span>
                          </span>
                          <span className="home-countdown-item">
                            <span className="home-countdown-value">{countdowns[event.id].minutes}</span>
                            <span className="home-countdown-label">min</span>
                          </span>
                        </div>
                      )}
                    </div>
                    <h3>{event.title}</h3>
                    <p className="home-event-description">{event.description}</p>
                    
                    <div className="home-event-details">
                      <p className="home-event-date">
                        <i className="fas fa-calendar-alt"></i>
                        {new Date(event.date).toLocaleDateString()}
                      </p>
                      <p className="home-event-location">
                        <i className="fas fa-map-marker-alt"></i>
                        {event.city && <span>{event.city}, </span>}
                        {event.state && <span>{event.state}, </span>}
                        {event.country ? getCountryName(event.country) : 'Location TBA'}
                      </p>
                    </div>
                    
                    {event.requirements && (
                      <div className="home-event-requirements">
                        <h4>Requirements:</h4>
                        <ul className="home-requirements-list">
                          {event.requirements.split('\n').filter(req => req.trim()).map((requirement, index) => (
                            <li key={index}>
                              <i className="fas fa-check-circle"></i>
                              {requirement.trim()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {event.partners && <EventPartners partnerIds={event.partners} />}
                    <div className="home-event-actions">
                      <button 
                        className="home-event-button home-event-button--view-details"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        View Details
                      </button>
                      {event.registrationLink && (
                        <a 
                          href={event.registrationLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="home-event-button home-event-button--register"
                        >
                          Register Now
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Removed the bottom navigation controls */}
        </div>
      )}

      {/* Activities Section */}
      {pageData.activities && pageData.activities.length > 0 && (
        <div className="activities-section">
          <div className="section-header">
            <h2>Recent Activities</h2>
            <Link to="/activities" className="view-all-link">
              View All <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <Cards activities={pageData.activities} />
        </div>
      )}

      {/* Members Section */}
      {pageData.members && pageData.members.length > 0 && (
        <div className="members-section">
          <div className="section-header">
            <h2>Our Board Members</h2>
            <Link to="/members" className="view-all-link">
              View All <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <Members members={pageData.members} />
        </div>
      )}

      {/* Partners Section */}
      <PartnersSection />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Home;